import { IYearMonth } from '@/coreDomainsModule/date';
import {
  InputExcelInfoByFileType,
  InputFileType,
  RegisterExcelInfo as DomainRegisterExcelInfo,
} from '@/coreDomainsModule/entities/inputExcelInfo';
import { RegisterOrderExcelInfos } from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import { HomeInputExcelInfoByFileType, RegisterExcelInfo } from '../pages/interfaces/HomeInput';
import { GetInputExcelInfoAdapter } from '../useCases/GetInputExcelInfoUseCase';

/**
 * 保存形式の入力エクセル設定をホーム画面表示用に変換するAdapter
 */
export class GetExcelInputInfoAdapter implements GetInputExcelInfoAdapter<HomeInputExcelInfoByFileType> {
  /**
   * 保存形式の入力エクセル設定をホーム画面表示用に変換する
   * @param inputExcelInfo - 保存形式の入力エクセル設定
   */
  adapt(inputExcelInfo: InputExcelInfoByFileType): HomeInputExcelInfoByFileType {
    const homeInputExcelInfo: HomeInputExcelInfoByFileType = {};

    if (inputExcelInfo.Inventory) homeInputExcelInfo.Inventory = this._toStandardHomeInputExcelInfo(inputExcelInfo.Inventory);
    if (inputExcelInfo.SalesResults) homeInputExcelInfo.SalesResults = this._toStandardHomeInputExcelInfo(inputExcelInfo.SalesResults);
    if (inputExcelInfo.Arrival) homeInputExcelInfo.Arrival = this._toStandardHomeInputExcelInfo(inputExcelInfo.Arrival);
    if (inputExcelInfo.Order) homeInputExcelInfo.Order = this._withOrderStatusValidateErrors(inputExcelInfo.Order);

    return homeInputExcelInfo;
  }

  /**
   * 在庫/販売実績/入荷の保存形式をホーム画面表示用に変換する
   * @param value - 保存形式の入力エクセル設定
   * @returns ホーム画面表示用の入力エクセル設定
   */
  private _toStandardHomeInputExcelInfo(value: DomainRegisterExcelInfo): RegisterExcelInfo {
    return {
      headerLineNumber: value.headerLineNumber,
      alternateHeaderInfos: value.alternateHeaderInfos.map((alternateHeaderInfo) => ({
        header: alternateHeaderInfo.header,
        value: alternateHeaderInfo.value,
        unused: alternateHeaderInfo.unused,
        validateError: false,
      })),
      selectYearMonthPattern: value.selectYearMonthPattern === 'SingleMonth' ? 'Inlined' : value.selectYearMonthPattern,
      inlinedYearMonthHeaderName: {
        headerName: value.inlinedYearMonthHeaderName ?? '',
        validateError: 'none',
      },
      inHeaderYearMonthInfo: {
        startColumnNumber: {
          raw: value.inHeaderYearMonthInfo?.startColumnNumber ?? '',
          validateError: 'none',
        },
        endColumnNumber: {
          raw: value.inHeaderYearMonthInfo?.endColumnNumber ?? '',
          validateError: 'none',
        },
        start: {
          raw: this._yearMonthToRawString(value.inHeaderYearMonthInfo?.start),
          validateError: 'none',
        },
        end: {
          raw: this._yearMonthToRawString(value.inHeaderYearMonthInfo?.end),
          validateError: 'none',
        },
      },
    };
  }

  /**
   * 年月情報を文字列に変換する(1~9は0埋め)
   * @param yearMonth - 年月情報
   */
  private _yearMonthToRawString(yearMonth?: IYearMonth): string {
    if (yearMonth == null) {
      return '';
    }
    return `${yearMonth.year}${yearMonth.month.toString().padStart(2, '0')}`;
  }

  /**
   * 発注ステータスルールに画面用バリデーション状態を補完する
   * @param orderExcelInfos - 保存済み発注エクセル設定一覧
   * @returns 画面用バリデーション状態を補完した発注ステータスルール
   */
  private _withOrderStatusValidateErrors(orderExcelInfos: RegisterOrderExcelInfos): RegisterOrderExcelInfos {
    return orderExcelInfos.map((orderExcelInfo) => ({
      ...orderExcelInfo,
      rowMappingRules: orderExcelInfo.rowMappingRules.map((rowMappingRule) => ({
        ...rowMappingRule,
        orderStatusRules: rowMappingRule.orderStatusRules.map((rule) => ({
          ...rule,
          conditions: rule.conditions.map((condition) => ({
            ...condition,
            validateError: condition.validateError ?? {
              header: 'none',
              value: 'none',
            },
          })),
        })),
      })),
    }));
  }
}
